'use client'
import styles from './OnboardingForm.module.scss'
import { type ReactElement, useContext, useEffect, useRef, useState } from 'react'
import type { Dict } from '@/src/helpers/common.types'
import PageGutter from '@/src/components/common/PageGutter'
import WhoIsItForForm from '@/src/components/common/OnboardingForm/1.WhoIsItFor/index.client'
import HomeCareStageForm from '@/src/components/common/OnboardingForm/2.HomeCareStage/index.client'
import MoreDetailsForm from '@/src/components/common/OnboardingForm/3.MoreDetails/index.client'
import ContactDetailsForm from '@/src/components/common/OnboardingForm/4.ContactDetails/index.client'
import { classNames } from '@/src/helpers/component'
import AGLogo from '@/src/components/common/AGLogo'
import CTAButton from '@/src/components/common/CTAButton'
import Form from '@/src/components/common/Form'
import { formDataJSON } from '@/src/components/common/Form/Form.client'
import PlaceholderTopNav from '@/src/components/common/PlaceholderTopNav'
import { DirectusContext } from '@/src/contexts/DirectusContext'
import { usePostHog } from 'posthog-js/react'
import type { Pages } from '@/src/generated/graphql'
import SVGCheck from '@/src/components/common/SVG/SVGCheck'
import { useRouter } from 'next/navigation'

const storageDataKey = 'ag-onboarding'

export type OnboardingProps = {
  shown: boolean
  step: number
  data: Dict<string | string[]>
}

const MORE_DETAILS_LABEL = 'More details'

export const BaseOnboardingSteps: Dict<(() => ReactElement | null) | null> = {
  'Who is it for': WhoIsItForForm,
  'Home care stage': HomeCareStageForm,
  [MORE_DETAILS_LABEL]: MoreDetailsForm,
  'Contact details': ContactDetailsForm,
  "What's next": null,
}

export const readStoredData = () => {
  if (typeof localStorage === 'undefined') return {} as OnboardingProps

  let storedData = localStorage[storageDataKey]

  if (storedData) {
    try {
      storedData = JSON.parse(storedData)
    } catch {}
  }

  if (!storedData) {
    storedData = {
      shown: false,
      step: 0,
      data: {},
    }
  }

  return storedData as OnboardingProps
}

export default function OnboardingForm() {
  const [onboardingSteps, setOnboardingSteps] = useState(BaseOnboardingSteps)
  const [onboardingData, setOnboardingData] = useState(() => ({
    ...readStoredData(),
    shown: false,
  }))
  const [currentStep, setCurrentStep] = useState(() => readStoredData()?.step || 0)
  const [isFormFulfilled, setIsFormFulfilled] = useState(false)
  const [isABControl, setABControl] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const directusContext = useContext(DirectusContext)
  const posthog = usePostHog()
  const router = useRouter()
  const isLastStep = Object.entries(onboardingSteps)[currentStep + 1]?.[1] === null
  const ref = useRef<HTMLDivElement>(null)
  const refScrolledStep = useRef(-1)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const storeData = (data: OnboardingProps) => {
    setOnboardingData(data)
    setCurrentStep(data.step || 0)
    localStorage[storageDataKey] = JSON.stringify(data)
  }

  useEffect(() => {
    const handleShowOnboarding = () => {
      storeData({ ...readStoredData(), shown: true })
      dialogRef.current?.showModal()
    }

    window.addEventListener('onboarding:show', handleShowOnboarding)
    return () => window.removeEventListener('onboarding:show', handleShowOnboarding)
  }, [])

  const handleControl = (isControl: boolean) => {
    if (!isControl) {
      const _onboardingSteps = { ...BaseOnboardingSteps }
      delete _onboardingSteps[MORE_DETAILS_LABEL]
      setOnboardingSteps(_onboardingSteps)
    }
    setABControl(isControl)
  }

  useEffect(() => {
    posthog.onFeatureFlags((_, variant) => {
      const isControl = (variant['onboarding-form'] || 'control') === 'control'
      handleControl(isControl)
    })
  }, [posthog, isABControl])

  useEffect(() => {
    if (refScrolledStep.current != currentStep) {
      refScrolledStep.current = currentStep
      ref.current?.scrollTo({ top: 0 })
    }
  }, [currentStep])

  const siteSettings = directusContext.SITE_SETTINGS

  if (
    !directusContext.SITE_SETTINGS.onboarding_confirmation_getting_started ||
    !directusContext.SITE_SETTINGS.onboarding_confirmation_employment ||
    !directusContext.SITE_SETTINGS.onboarding_confirmation_switch_provider ||
    !directusContext.SITE_SETTINGS.onboarding_confirmation_with_funding ||
    !directusContext.SITE_SETTINGS.onboarding_confirmation_already_assessed ||
    !directusContext.SITE_SETTINGS.onboarding_confirmation_not_yet_assessed
  ) {
    throw new Error('Onboarding Confirmation pages not yet completely setup in Site Settings')
  }

  if (!directusContext.SITE_SETTINGS.work_with_us_page) {
    throw new Error(
      'Work With Us page (page with a Work With Us form) not yet configured in Site Settings',
    )
  }

  const currentPageSlug = (directusContext.PAGE_DATA as Pages).slug
  const isOnboardingConfirmationPage = [
    directusContext.SITE_SETTINGS.onboarding_confirmation_getting_started.slug,
    directusContext.SITE_SETTINGS.onboarding_confirmation_employment.slug,
    directusContext.SITE_SETTINGS.onboarding_confirmation_switch_provider.slug,
    directusContext.SITE_SETTINGS.onboarding_confirmation_with_funding.slug,
    directusContext.SITE_SETTINGS.onboarding_confirmation_already_assessed.slug,
    directusContext.SITE_SETTINGS.onboarding_confirmation_not_yet_assessed.slug,
  ].includes(currentPageSlug)

  useEffect(() => {
    if (isOnboardingConfirmationPage) window.scrollTo({ top: 0, behavior: 'instant' })
  }, [isOnboardingConfirmationPage])

  useEffect(() => {
    if (onboardingData.shown) {
      dialogRef.current?.showModal()
    } else {
      setTimeout(() => {
        dialogRef.current?.close()
      }, 500) // allows dialog close animation to play before actually closing
    }
  }, [onboardingData.shown])

  const renderSteps = () => (
    <div className={styles.stepsContainer}>
      <PageGutter className={styles.stepsGuttered}>
        {Object.entries(onboardingSteps).map(([stepLabel], idx) => {
          const isCompleted =
            (!onboardingData.shown && isOnboardingConfirmationPage) || idx < currentStep
          return (
            <div
              key={idx}
              className={classNames(
                styles.stepLabel,
                isCompleted
                  ? styles.completed
                  : idx == currentStep
                    ? styles.current
                    : styles.pending,
              )}>
              <div className={styles.stepNumber}>{isCompleted ? <SVGCheck /> : idx + 1}</div>
              <div className={styles.stepLabelText}>{stepLabel}</div>
            </div>
          )
        })}
      </PageGutter>
    </div>
  )

  if (!onboardingData.shown) {
    if (isOnboardingConfirmationPage) return renderSteps()
    return null
  }

  if (
    [
      siteSettings.privacy_policy_page?.slug,
      siteSettings.referral_terms_and_conditions_page?.slug,
    ].includes(currentPageSlug)
  )
    return null

  const FormFields = Object.entries(onboardingSteps)[currentStep][1]
  const onboardingConfirmationPage = (() => {
    switch (onboardingData.data?.['homecare_stage']) {
      case 'getting_started':
        return directusContext.SITE_SETTINGS.onboarding_confirmation_getting_started

      case 'wait_funding':
        return onboardingData.data['more_details__waiting_hcp__phone_assessment'] === 'no'
          ? directusContext.SITE_SETTINGS.onboarding_confirmation_not_yet_assessed
          : directusContext.SITE_SETTINGS.onboarding_confirmation_already_assessed

      case 'switch_provider':
        return directusContext.SITE_SETTINGS.onboarding_confirmation_switch_provider

      case 'with_homecare_package':
        return directusContext.SITE_SETTINGS.onboarding_confirmation_with_funding

      case 'employment':
        return directusContext.SITE_SETTINGS.onboarding_confirmation_employment
    }
  })()

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isFormFulfilled) return false // if there are errors in the inputs, let the browser show the errors

    const form = e.currentTarget.closest('form')

    if (!form) return false

    const updatedData = {
      ...onboardingData,
      step: currentStep + 1,
      data: {
        ...onboardingData.data,
        ...formDataJSON(form),
      },
    }

    if (updatedData.data?.['homecare_stage'] === 'employment') {
      delete localStorage[storageDataKey]
      router.push(`/${directusContext.SITE_SETTINGS.work_with_us_page?.slug}/`)
    } else {
      storeData(updatedData)
    }
  }

  const beforeSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    if (!isFormFulfilled || !isLastStep) return false // if there are errors in the inputs, let the browser show the errors

    const form = e.currentTarget.closest('form')

    if (!form) return false

    const updatedData = {
      ...onboardingData,
      shown: false,

      step: currentStep + 1,

      data: {
        ...onboardingData.data,
        ...formDataJSON(form),
      },
    }

    localStorage[storageDataKey] = JSON.stringify(updatedData)
    setIsSubmitting(true)

    return true
  }

  return (
    <dialog ref={dialogRef} aria-label="Onboarding form" className={styles.modalContainer}>
      <div ref={ref} className={classNames('modal-shown', styles.container)}>
        <PlaceholderTopNav>
          <PageGutter className={styles.topnavGutter}>
            <AGLogo
              onClick={() => {
                if (location.pathname == '/') {
                  storeData({} as OnboardingProps)
                } else {
                  delete localStorage[storageDataKey]
                }
              }}
            />
            <CTAButton
              text={'Cancel Form'}
              onClick={e => {
                e.preventDefault()
                storeData({ shown: false, step: 0, data: {} })
                if (isOnboardingConfirmationPage) router.push('/')
              }}
            />
          </PageGutter>
        </PlaceholderTopNav>
        {renderSteps()}
        <PageGutter className={styles.formGutter}>
          <Form
            className={styles.form}
            formName={'lead_form'}
            afterSubmitted={({ success }) => {
              if (success) {
                delete localStorage[storageDataKey]
                setOnboardingData({ shown: false, step: 0, data: {} })
                setCurrentStep(0)
                router.push(`/${onboardingConfirmationPage?.slug}/`)
              } else {
                setIsSubmitting(false)
              }
            }}
            beforeSubmit={beforeSubmit}
            afterChanged={_ => setIsFormFulfilled(_.isComplete)}
            dataForSubmission={() => readStoredData().data}>
            <div className={styles.formContent}>{FormFields && <FormFields />}</div>
            <hr />
            <div className={styles.navButtons}>
              <button
                type="button"
                className={currentStep === 0 ? styles.hidden : undefined}
                onClick={e => {
                  e.preventDefault()
                  if (currentStep > 0) storeData({ ...onboardingData, step: currentStep - 1 })
                }}>
                BACK
              </button>
              {!isLastStep ? (
                <button
                  onClick={handleNext}
                  type={'button'}
                  className={classNames(!isFormFulfilled ? styles.disabled : '')}
                  disabled={isSubmitting}>
                  {'NEXT'}
                </button>
              ) : (
                <button
                  type={'submit'}
                  className={classNames(!isFormFulfilled || isSubmitting ? styles.disabled : '')}
                  disabled={isSubmitting}>
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                </button>
              )}
            </div>
          </Form>
        </PageGutter>
      </div>
    </dialog>
  )
}
